'use client'

import { reportHandledException } from '@/lib/error-reporting'
import { useEffect } from 'react'
import { fetchMe, onAuthTokenChange, readStoredToken } from '@/features/auth/auth-storage'
import { ANALYTICS_READY_EVENT } from '@/lib/analytics'
import { syncAnalyticsIdentity, type AnalyticsIdentityUser } from './analytics-identity'
import { getOrCaptureFirstTouch } from './first-touch'

/**
 * Keeps GA's User-ID and Meta's Advanced Matching in step with who is signed in.
 *
 * Mounted ONCE beside `<AnalyticsScripts>` in the root layout rather than being
 * wired into each auth entry point. There are several ways to become signed in
 * — the OTP modal, the signup page, a magic link, an invite accept — and every
 * one of them ends in the same place: a token in storage. `onAuthTokenChange`
 * covers all of them, including ones added later and including a sign-out in
 * ANOTHER TAB, which a shared device makes more than theoretical.
 *
 * Renders nothing.
 */
export function AnalyticsIdentitySync() {
  // Record where this browser came from, before anything else runs. This
  // component is the one place guaranteed to mount exactly once per full page
  // load on every route (root layout), which is what "first touch" needs: the
  // URL that brought someone here, not the one they happen to be on when they
  // eventually sign up. A stored touch short-circuits, so this is a cookie read
  // on every visit after the first.
  useEffect(() => {
    getOrCaptureFirstTouch()
  }, [])

  useEffect(() => {
    let cancelled = false
    /**
     * Which lookup owns the result. `cancelled` alone only covers unmount, and
     * the damaging race is between two live lookups: sign out while a sign-in's
     * `/me` is still in flight and the slow reply would re-identify a visitor
     * who has already left, restoring their address for the next page load.
     */
    let generation = 0
    /** The last resolved identity, so a re-apply costs no second `/me`. */
    let resolved: AnalyticsIdentityUser | null | undefined
    /** Which token {@link resolved} describes — see the catch below. */
    let resolvedToken: string | null | undefined

    const applyResolved = (identity: AnalyticsIdentityUser | null) => {
      resolved = identity
      void syncAnalyticsIdentity(identity)
    }

    const apply = async () => {
      const mine = ++generation
      const token = readStoredToken()
      if (!token) {
        resolvedToken = null
        applyResolved(null)
        return
      }
      try {
        const me = await fetchMe(token)
        if (cancelled || mine !== generation) return
        resolvedToken = token
        applyResolved({ id: me.user.id, email: me.user.email })
      } catch (cause) {
        reportHandledException(cause, { source: 'lib/identity/analytics-identity-sync.tsx' })
        // Report + continue. A dead token is ordinary (the shared client has
        // already evicted it), and identity resolution is an optimisation —
        // neither is a reason to disturb the page.
        console.warn('[identity] could not resolve the signed-in user for analytics:', cause)
        if (cancelled || mine !== generation) return
        // Only drop the cached identity when it describes a DIFFERENT token.
        // A failure on the SAME token is a transient one — a network blip, a
        // recoverable context rejection — and the person is still who they
        // were, so clearing would turn the rest of this page load anonymous
        // for no reason. A failure on a NEW token means whoever is cached is
        // known to be wrong, and leaving them would let the replay below
        // re-assert them for whoever is browsing now.
        if (resolvedToken !== token) {
          resolvedToken = token
          applyResolved(null)
        }
      }
    }

    /**
     * GA loads `afterInteractive`, i.e. AFTER this effect runs, and
     * `gtag('set', …)` silently does nothing while `window.gtag` is undefined.
     * Without this the User-ID would be dropped for the whole page load
     * whenever the lookup wins that race, and only the next sign-in would
     * repair it. Replays the identity already resolved, so it costs no request.
     */
    const reapply = () => {
      if (resolved !== undefined) void syncAnalyticsIdentity(resolved)
    }

    void apply()
    const unsubscribe = onAuthTokenChange(apply)
    window.addEventListener(ANALYTICS_READY_EVENT, reapply)
    return () => {
      cancelled = true
      unsubscribe()
      window.removeEventListener(ANALYTICS_READY_EVENT, reapply)
    }
  }, [])

  return null
}
